import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { usePermissions } from "@/hooks/usePermissions";
import { hasLegacyBillingAccess, useBillingStatus } from "./BillingStatusContext";
import type { BuildingPlanTier } from "./types";
import {
  BuildingCostConsentDialog,
  type BuildingCostIntent,
} from "./components/BuildingCostConsentDialog";

export interface BuildingCostConsent {
  /** Resolves true when the cost has been agreed, or when none applies. */
  request: (intent: BuildingCostIntent) => Promise<boolean>;
  requestPlan: () => Promise<BuildingPlanTier | null>;
  /** Render this wherever the caller lives; it is nothing until asked. */
  dialog: ReactNode;
}

/**
 * The consent step, as one line at each call site.
 *
 * Enrolled owner creation requests an explicit plan, including Free. The
 * administrator-controlled legacy exemption keeps the original creation flow.
 * Paid choices retain the existing company, payment and read-only gates.
 */
export function useBuildingCostConsent(): BuildingCostConsent {
  const billing = useBillingStatus();
  const legacy = hasLegacyBillingAccess(billing);
  const permissions = usePermissions();
  const [dialogState, setDialogState] = useState<{ open: boolean; intent: BuildingCostIntent }>({
    open: false,
    intent: { mode: "add" },
  });
  const resolver = useRef<((agreed: boolean) => void) | null>(null);
  const selectedTier = useRef<BuildingPlanTier | null>(null);
  const requestedOrgId = useRef<string | undefined>();
  const isManager = permissions.isOrgOwner || permissions.isOrgAdmin;
  const required = !legacy && !billing.isLoading && !billing.isPilot && isManager;

  const settle = useCallback((agreed: boolean) => {
    // Radix keeps the content mounted during its exit animation. Keep the
    // agreed intent so closing a Free quote cannot render a legacy paid quote.
    setDialogState((current) => ({ ...current, open: false }));
    const resolve = resolver.current;
    resolver.current = null;
    resolve?.(agreed);
  }, []);

  useEffect(() => {
    if (dialogState.open && requestedOrgId.current !== billing.orgId) settle(false);
  }, [billing.orgId, dialogState.open, settle]);

  useEffect(
    () => () => {
      resolver.current?.(false);
      resolver.current = null;
    },
    []
  );

  const request = useCallback(
    (next: BuildingCostIntent) => {
      if (legacy) return Promise.resolve(true);
      if (!required && !(next.mode === "add" && next.choosePlan)) return Promise.resolve(true);
      // A second request while one is open would strand the first caller's
      // promise for ever, and the button that opened it stays disabled.
      resolver.current?.(false);
      requestedOrgId.current = billing.orgId;
      setDialogState({ open: true, intent: next });
      return new Promise<boolean>((resolve) => {
        resolver.current = resolve;
      });
    },
    [required, billing.orgId, legacy]
  );

  return {
    request,
    requestPlan: async () => {
      if (!permissions.isOrgOwner || legacy) return null;
      selectedTier.current = null;
      const agreed = await request({ mode: "add", choosePlan: true });
      return agreed ? selectedTier.current : null;
    },
    dialog: (
      <BuildingCostConsentDialog
        open={dialogState.open}
        intent={dialogState.intent}
        onCancel={() => settle(false)}
        onAgree={(tier) => {
          selectedTier.current = tier ?? null;
          settle(true);
        }}
      />
    ),
  };
}
